import { AxiosRequestConfig } from 'axios';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { axiosInstance } from '../../config/axiosInstance';
import { globalEnv } from '../../config/baseConfig';
import { sortingOrder } from '../Utils/sortingOrder';
import { useTypedSelector } from '../../hooks/useTypeSelector';
import { useDispatch } from 'react-redux';
import { setSelectedSection, setTab } from '../../redux/actions/sideBarAction';
import { setSearchPlaceholderAction } from '../../redux/actions/searchBarAction';
import { HeaderItem } from '../Tables/TablesList/Components/common';
import { useNavigate } from 'react-router-dom';
import LazyImage from '../Utils/Components/imageComponent';
import placeholder from '../../assets/images/placeholder.png';
import moment from 'moment';
import TableLoadingSkeleton from '../Utils/LoadingAnimations/TableLoadingSkeleton';
import { DataNotFoundAnimation } from '../Utils/ErrorAnimations/dataNotFoundAnim';
import Pagination from '../Tables/pagination';
import ParentModal from '../Utils/Components/parentModal';
import CreateExtensionModal from './modal/createExtensionModal';
type TopicType = {
	_id: string;
	name: string;
	coverPicture: picType;
};

type picType = {
	url_low_res: string;
	url: string;
};

type ExtensionType = {
	_id: string;
	name: string;
	topicData: TopicType;
	userCount: number;
	createdAt: string;
};

export const ExtensionsMain = () => {
	const [extensionData, setExtensionData] = useState<ExtensionType[]>();
	const [page, setPage] = useState(1);

	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('lastActive');
	const [count, setCount] = useState(0);

	const [isWarnModalOpen, setIsWarnModalOpen] = useState(false);
	const [isAddExtensionModalOpen, setIsAddExtensionModalOpen] = useState(false);
	const [isChanged, setIsChanged] = useState(false);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const dispatch = useDispatch();

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	const queryClient = useQueryClient();
	useEffect(() => {
		dispatch(setSelectedSection('All Extensions'));
		dispatch(setTab('extensions'));
		dispatch(setSearchPlaceholderAction('Search for extensions'));

		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isSuccess, isError, isFetching, isLoading } = useQuery(
		[page, selectedHeader, sortOrder, searchQuery],
		() => fetchExtensions(page),

		{ keepPreviousData: true, staleTime: 5000, refetchOnWindowFocus: false }
	);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(['topics', page + 1], () =>
				fetchExtensions(page + 1)
			);
		}
	}, [data, page, queryClient]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			window.location.reload();
		}, 1000 * 60 * 20);
		return () => clearInterval(intervalId);
	}, []);

	async function fetchExtensions(page: number) {
		const config: AxiosRequestConfig = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};

		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/extension/all?page=${page}&sortby=${selectedHeader}&order=${sortOrder}${
					searchQuery !== '' ? '&filter=' + searchQuery : ''
				}`,
			config
		);

		setExtensionData(data.extensions);
		setCount(data.meta.total);

		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Extension name"
					slugName="name"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Extension topic name"
					slugName="topicName"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Users count"
					slugName="usersCount"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Creation date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<th className="absolute p-2 whitespace-nowrap right-0">
					<div className="font-semibold text-left">{`Count: ${count}`}</div>
				</th>
			</tr>
		);
	};

	return (
		<section>
			<div className="p-3 mt-16 ml-18">
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					isSuccess && (
						<div>
							<ParentModal
								visible={isAddExtensionModalOpen}
								setModalOpen={setIsAddExtensionModalOpen}
								isChanged={false}
								setIsChanged={setIsChanged}
								isWarnModalOpen={isWarnModalOpen}
								setIsWarnModalOpen={setIsWarnModalOpen}
							>
								<CreateExtensionModal />
							</ParentModal>

							<div className="overflow-x-auto ">
								<div
									onClick={() => setIsAddExtensionModalOpen(true)}
									className={`flex relative justify-center items-center py-1.5 px-1 rounded-full
									w-fit transition-all duration-500 max-h-7 cursor-pointer group
									hover:bg-purple-600 dark:hover:bg-purple-500 bg-gray-400 dark:bg-gray-600
									border-green-200 dark:text-gray-300 text-gray-600 hover:text-gray-200`}
								>
									<span className="text-xs font-bold mx-1">
										Add extension ➕
									</span>
								</div>

								<table className="table-auto w-full">
									<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light ">
										<TableHeaders />
									</thead>
									<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
										{extensionData != null &&
											extensionData.map((item: ExtensionType) => (
												<TableData
													key={item._id}
													id={item._id}
													name={item.name}
													createdAt={item.createdAt}
													userCount={item.userCount}
													topic={item.topicData}
												/>
											))}
									</tbody>
								</table>
							</div>

							{data.meta != null && (
								<Pagination
									itemsPerPage={data.meta.limit}
									totalCount={data.meta.total}
									totalPages={data.meta.pages}
									currentPage={page}
									setPage={setPage}
								/>
							)}
						</div>
					)
				)}
			</div>
		</section>
	);
};

type tableDateType = {
	id: string;
	name: string;
	topic: TopicType;
	createdAt: string;
	userCount: number;
};

const TableData = ({
	id,
	name,
	topic,
	createdAt,
	userCount
}: tableDateType) => {
	const navigate = useNavigate();
	return (
		<tr
			className="cursor-pointer"
			onClick={() => {
				navigate(`../extensions/${id}/select-menu`);
			}}
		>
			<td className="col-span-1 p-2 break-all w-80">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage
							photo={
								topic.coverPicture != null
									? topic.coverPicture.url_low_res != null
										? topic.coverPicture.url_low_res
										: placeholder
									: placeholder
							}
							style={
								'block object-cover object-center w-full aspect-[1/1] rounded-lg'
							}
						/>
					</div>
					<div className="font-medium text-gray-800 dark:text-white">
						{name}
					</div>
				</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{topic.name}
				</div>
			</td>

			<td className="col-span-1 p-2 break-all w-20">
				<div className="text-center dark:text-gray-400">{userCount}</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(createdAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
		</tr>
	);
};
